import React from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { reduxForm, InjectedFormProps } from "redux-form";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import "../../../App.css";
import "../../../css/bootstrap.scss";
import welcome_banner from "../../../images/welcome_screen_banner.png";
import icon1 from "../../../images/welcome_screen_icon1.png";
import icon2 from "../../../images/welcome_screen_icon2.png";
import buy_crypto from "../../../images/buy_crypto.png";
import verify_identity_icon from "../../../images/verify_identity_icon.png";
import account_created_icon from "../../../images/account_created_icon.png";
import Spacer from "MyComponents/UI/Spacer";
import { resetAllFields } from "../../authslice";
import axios from "axios";
import config from "../../../config";
import { notifyError, notifySuccess } from "MyComponents/Toaster";

interface FormData {
  email: string;
  password: string;
}

interface Props { }

const WelcomeScreen: React.FC<Props & InjectedFormProps<FormData, Props>> = (props) => {
  const { t } = useTranslation();
  const { handleSubmit } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useSelector((state: RootState) => state.theme.theme);
  const userId = useSelector((state: RootState) => state.auth.userID);
  const kycToken = useSelector((state: RootState) => state.auth.kycToken);
  const displayName = useSelector((state: RootState) => state.auth.firstName + " " + state.auth.lastName);
  const email = useSelector((state: RootState) => state.auth.email);

  localStorage.removeItem("submittedCount");

  const submitForm = async () => {
    const kycUrl = `/kyc/personal/${encodeURIComponent(userId)}/${encodeURIComponent(kycToken)}`;
    // Reset now that userId/kycToken have been consumed into the URL above — the KYC
    // wizard reads them from the route, not from this (about-to-be-cleared) auth slice.
    dispatch(resetAllFields());
    navigate(kycUrl, { replace: true });
  };

  const createProfile = async () => {
    try {
      const response = await axios.post(`${config.NODE_API_BASE_URL}/profile/create`, {
        wso2_user_id: userId,
        email: email,
        account_type: "personal",
        display_name: displayName,
        is_default: true,
      });
      if (response.status === 201) {
        notifySuccess("Profile created successfully");
        return true;
      }
    } catch (error) {
      console.error("Error creating profile:", error);
      notifyError("Failed to create profile");
      return false;
    }
    return false;
  };

  const redirectUser = async () => {
    await createProfile();
    try {
      axios.post(`${config.NODE_API_BASE_URL}/wso2/sendKycEmail`, {
        email, userId, kycToken, accountType: "personal"
      }).catch(err => console.error("Error sending KYC email:", err));
    } catch (error) {
      console.error("Error triggering KYC email:", error);
    }
    dispatch(resetAllFields());
    navigate("/personal_account_created");
  };

  return (
    <>
      <Container fluid className="px-0">
        <Form noValidate onSubmit={handleSubmit(submitForm)}>
          <Row className="g-0">
            <Col md={12} lg={6} className="px-3 px-md-5">
              <div className="mt-4 mx-0 mx-md-4">
                <p className="text-primary text-uppercase fw-bold" style={{ letterSpacing: "1px" }}>
                  {t('welcome.created')}
                </p>
                <h3>{t('welcome.title')}</h3>
                <p className="mt-3 text-secondary">{t('welcome.subtitle')}</p>
                
                <div className={`d-flex align-items-start p-3 mt-3 rounded-4 welcome-screen-card ${theme === "dark" && "bg-black"}`} style={{ border: "1px solid #f1f5f9" }}>
                  <img src={icon1} width={40} className="me-3 mt-1" alt="icon1" />
                  <div>
                    <h6 className="text-secondary fw-bold mb-1">{t('welcome.feature1_title')}</h6>
                    <p className="text-secondary m-0 small">{t('welcome.feature1_desc')}</p>
                  </div>
                </div>

                <div className={`d-flex align-items-start p-3 mt-3 rounded-4 welcome-screen-card ${theme === "dark" && "bg-black"}`} style={{ border: "1px solid #f1f5f9" }}>
                  <img src={buy_crypto} width={40} className="me-3 mt-1" alt="buy_crypto" />
                  <div>
                    <h6 className="text-secondary fw-bold mb-1">{t('welcome.feature2_title')}</h6>
                    <p className="text-secondary m-0 small">{t('welcome.feature2_desc')}</p>
                  </div>
                </div>

                <div className={`d-flex align-items-start p-3 mt-3 rounded-4 welcome-screen-card ${theme === "dark" && "bg-black"}`} style={{ border: "1px solid #f1f5f9" }}>
                  <img src={icon2} width={40} className="me-3 mt-1" alt="icon2" />
                  <div>
                    <h6 className="text-secondary fw-bold mb-1">{t('welcome.feature3_title')}</h6>
                    <p className="text-secondary m-0 small">{t('welcome.feature3_desc')}</p>
                  </div>
                </div>

                <Row className="mt-4 mb-5">
                  <Col md={5} className="mb-4">
                    <Button type="submit" className="btn btn-primary py-3 w-100 mt-3 rounded-5" style={{ backgroundColor: "#00D084", borderColor: "#00D084", color: "#000000", fontWeight: "bold" }}>
                      {t('welcome.verify_now')}
                    </Button>
                  </Col>
                  <Col md={7} />
                  <Col md={12} className="text-center text-md-start">
                    <Link to="" onClick={redirectUser} style={{ color: "#00D084", fontWeight: "bold", textDecoration: "none" }}>{t('welcome.skip')}</Link>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col md={7} className="text-center d-none d-md-block">
              <Spacer height="150px" />
              <img src={welcome_banner} width={450} alt="welcome_banner" />
              <Spacer height="40px" />
              <img src={account_created_icon} width={200} alt="account_created_icon" />
              <img src={verify_identity_icon} width={200} alt="verify_identity_icon" />
            </Col>
          </Row>
        </Form>
      </Container>
    </>
  );
};

const form = reduxForm<FormData, Props>({
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  form: "wizard",
  enableReinitialize: true,
})(WelcomeScreen);

export default form;
